import { useCallback, useRef } from 'react';

import { logger } from '@zep/core-logger';

import { useGenerateSimliarQuiz } from '@/api/ai/useGenerateSimliarQuiz';
import { useLocale } from '@/hooks';
import { useAiGenerateSimilarQuizErrorAlert } from '@/views/build/modal/BuildAiModal/useAiGenerateSimilarQuizErrorAlert';

import { useAiQuizGeneration } from '../../hook/useAiQuizGeneration';
import { useSelectSubject } from '../SectionBasicInfo/useSelectSubject';

export const useAiGenerateSimilarQuiz = ({
  question,
}: {
  question: string;
}) => {
  const {
    mutateAsync: generateSimilarQuiz,
    status,
    reset,
    variables,
  } = useGenerateSimliarQuiz();
  const previousPrompt = useRef<string>('');

  const { subjectName } = useSelectSubject();
  const { locale } = useLocale();
  const alertError = useAiGenerateSimilarQuizErrorAlert();

  const {
    generatedItems,
    handleGenerateSuccess,
    handleReset: handleResetAiQuizGeneration,
    handleDeleteItem,
  } = useAiQuizGeneration();

  const handleReset = useCallback(() => {
    handleResetAiQuizGeneration();
    reset();
  }, [handleResetAiQuizGeneration, reset]);

  const handleGenerate = useCallback(
    async (
      prompt: string,
      keywordInfo?: {
        keyword: string;
        keywordIndex: number;
      },
    ) => {
      logger.track(
        keywordInfo ? 'ai_유사문제_키워드_시작' : 'ai_유사문제_시작',
      );
      try {
        const start = new Date().getTime();
        const { questions } = await generateSimilarQuiz({
          question,
          category: subjectName ?? '',
          locale: locale,
          prompt,
        });
        previousPrompt.current = prompt;
        handleGenerateSuccess(questions);
        const end = new Date().getTime();
        const duration = ((end - start) / 1000).toFixed(2);

        if (keywordInfo) {
          logger.track('ai_유사문제_키워드_완료', {
            keyword: keywordInfo.keyword,
            keywordIndex: keywordInfo.keywordIndex,
            duration,
          });
        } else {
          logger.track('ai_유사문제_시작_완료', {
            prompt,
            duration,
          });
        }
      } catch (error) {
        alertError(
          error,
          keywordInfo ? 'ai_유사문제_키워드_에러' : 'ai_유사문제_시작_에러',
        );
      }
    },
    [
      generateSimilarQuiz,
      question,
      locale,
      subjectName,
      alertError,
      handleGenerateSuccess,
    ],
  );

  return {
    handleReset,
    handleGenerate,
    status,
    variables,
    generatedItems,
    previousPrompt,
    handleDeleteItem,
  };
};
